/**
 * [INPUT]: 依赖 AuthContext 的管理员用户名密码动作与本地表单状态
 * [OUTPUT]: 对外提供单管理员后台登录页，展示登录错误、提交中和安全提示
 * [POS]: admin-web 未认证入口，与平台玩家登录流程隔离
 * [PROTOCOL]: 变更时更新此头部，然后检查 CLAUDE.md
 */
import { FormEvent, useState } from 'react';
import { ArrowRight, LockKeyhole, ShieldCheck, Sparkles } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isAdminSession, useAuth } from '../context/auth';
import { Button, Input } from '../components/ui';

export function LoginPage() {
  const { session, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [identifier, setIdentifier] = useState('');
  const [secret, setSecret] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (isAdminSession(session)) {
    navigate('/', { replace: true });
    return null;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(identifier, secret);
      const destination = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? '/';
      navigate(destination, { replace: true });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : '登录失败，请重试。');
    } finally {
      setLoading(false);
    }
  }

  return <div className="grid min-h-screen bg-ink lg:grid-cols-[minmax(0,0.92fr)_minmax(460px,1.08fr)]">
    <div className="relative hidden overflow-hidden p-10 text-white lg:flex lg:flex-col lg:justify-between xl:p-14">
      <div className="absolute -left-32 top-1/4 h-96 w-96 rounded-full bg-moss/30 blur-3xl" /><div className="absolute -right-28 bottom-10 h-72 w-72 rounded-full bg-mint/10 blur-3xl" />
      <div className="relative flex items-center gap-3"><img src="/elseyou-brand.png" alt="" className="h-10 w-10 rounded-xl object-cover" /><div><p className="text-sm font-bold tracking-tight">ELSEYOU</p><p className="font-mono text-[9px] uppercase tracking-[0.18em] text-white/40">control plane</p></div></div>
      <div className="relative max-w-lg"><p className="font-mono text-[11px] uppercase tracking-[0.22em] text-mint">One thousand worlds, one calm console.</p><h1 className="mt-5 text-4xl font-semibold leading-tight tracking-[-0.04em] xl:text-6xl">把混沌的故事，<br /><span className="text-mint">整理成可运行的世界。</span></h1><p className="mt-7 max-w-md text-sm leading-7 text-white/55">管理版本、观察 AI 消耗、照看每一个由想象长出来的文游。</p></div>
      <div className="relative flex items-center gap-3 text-xs text-white/35"><ShieldCheck className="h-4 w-4" />内部工作区 · 仅限授权管理员</div>
    </div>
    <div className="flex min-h-screen items-center justify-center bg-paper px-5 py-10 sm:px-8">
      <div className="w-full max-w-[410px]">
        <div className="mb-10 flex items-center gap-3 lg:hidden"><img src="/elseyou-brand.png" alt="" className="h-9 w-9 rounded-xl object-cover" /><div><p className="text-sm font-bold tracking-tight">ELSEYOU</p><p className="font-mono text-[9px] uppercase tracking-[0.18em] text-slate-400">control plane</p></div></div>
        <div className="mb-8"><div className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-mint text-moss"><LockKeyhole className="h-5 w-5" /></div><h2 className="text-2xl font-semibold tracking-[-0.03em] text-ink">欢迎回来</h2><p className="mt-2 text-sm leading-6 text-slate-500">登录后台，继续照看你的游戏世界。</p></div>
        <form className="space-y-5" onSubmit={handleSubmit}>
          <label className="block"><span className="mb-2 block text-xs font-semibold text-slate-600">管理员用户名</span><Input type="text" autoComplete="username" value={identifier} onChange={(event) => setIdentifier(event.target.value)} placeholder="admin" /></label>
          <label className="block"><span className="mb-2 block text-xs font-semibold text-slate-600">管理员密码</span><Input type="password" autoComplete="current-password" value={secret} onChange={(event) => setSecret(event.target.value)} placeholder="请输入密码" /></label>
          {error && <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-xs leading-5 text-rose-700">{error}</div>}
          <Button className="w-full" size="lg" disabled={loading}>{loading ? <><span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />验证中…</> : <>进入控制台 <ArrowRight className="h-4 w-4" /></>}</Button>
        </form>
        <div className="mt-8 flex items-start gap-2.5 rounded-xl border border-line bg-white p-3.5 text-xs leading-5 text-slate-500"><Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-amber" /><p>管理员凭据由局域网 server 的环境变量校验，登录后建立独立的 Authorization 会话。</p></div>
        <p className="mt-10 text-center font-mono text-[10px] uppercase tracking-[0.16em] text-slate-400">ELSEYOU / PRIVATE ADMIN</p>
      </div>
    </div>
  </div>;
}
